"use client"

import { useState } from 'react'
import { Bell, BellRing, CheckCircle2, Calendar, MapPin, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ZipCodeInput } from '@/components/ui/zip-code-input'
import {
  REMINDER_TOPIC_COPY,
  type ReminderTopic,
} from '@/data/blog-reminder-widgets'
import { analytics } from '@/lib/analytics'
import { PREMIUM_TRIAL_DAYS } from '@/lib/auth/feature-flags'
import { detectEmailTypo, type EmailSuggestion } from '@/lib/email-typo-detection'
import { useImpressionEvent } from '@/hooks/useImpressionEvent'

interface ReminderWidgetProps {
  /** Topic key passed through as `variant` from the inline-tool-widget marker */
  variant?: string
  /** Article slug; tagged into the subscription so we can pivot per-source if needed */
  slug?: string
  /**
   * Explicit grass tag, overriding the topic-derived `TOPIC_GRASS_TYPE`. Fixed
   * slots that know the grass (e.g. a grass guide) pass it so the welcome email
   * + Resend contact are personalized.
   */
  grassType?: string
  /**
   * Overrides the signup context/source. Defaults to `blog_reminder_widget_${topic}`
   * for marker usage; fixed slots pass their existing context (e.g. "guide-read")
   * so PostHog source buckets stay continuous for pre/post comparison.
   */
  source?: string
  /** Label for the impression event so we can segment views by slot. */
  placement?: string
}

const ZIP_REGEX = /^\d{5}$/
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Grass-specific topics carry a grass tag so the welcome-email subject and the
// Resend contact are personalized ("...for your Bermuda lawn"). Non-grass topics
// (pre-emergent / weed-feed / aeration / overseeding / feeding) intentionally
// have none — they're not grass-type-specific. Display names match the grass-ID
// convention (normalizeGrassKey maps "St. Augustine" downstream).
const TOPIC_GRASS_TYPE: Partial<Record<ReminderTopic, string>> = {
  'bermuda-fertilizer': 'Bermuda',
  'centipede-fertilizer': 'Centipede',
  'st-augustine-fertilizer': 'St. Augustine',
}

function isReminderTopic(value: string | undefined): value is ReminderTopic {
  return !!value && value in REMINDER_TOPIC_COPY
}

export function ReminderWidget({ variant, slug, grassType, source, placement }: ReminderWidgetProps) {
  const topic: ReminderTopic = isReminderTopic(variant) ? variant : 'pre-emergent'
  const copy = REMINDER_TOPIC_COPY[topic]
  // Signup source: fixed slots override it (e.g. "guide-read") so PostHog buckets stay
  // continuous; marker usage defaults to the topic-level source.
  const signupSource = source ?? `blog_reminder_widget_${topic}`

  const [email, setEmail] = useState('')
  const [zip, setZip] = useState('')
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [emailSuggestion, setEmailSuggestion] = useState<EmailSuggestion | null>(null)

  // Impression source is topic-level to match the `newsletter_signup` source on
  // line ~103 (the analytics source drops the slug that the API context keeps);
  // slug rides along as a prop for per-post breakdowns.
  const impressionRef = useImpressionEvent<HTMLDivElement>(
    signupSource,
    { topic, slug, ...(placement ? { placement } : {}) },
  )

  function handleEmailChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value
    setEmail(value)
    if (status === 'error') {
      setStatus('idle')
      setErrorMessage(null)
    }
    setEmailSuggestion(value.includes('@') && value.includes('.') ? detectEmailTypo(value) : null)
  }

  function handleAcceptSuggestion() {
    if (emailSuggestion) {
      setEmail(emailSuggestion.suggested)
      setEmailSuggestion(null)
      if (status === 'error') {
        setStatus('idle')
        setErrorMessage(null)
      }
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (status === 'submitting' || status === 'success') return

    if (!ZIP_REGEX.test(zip)) {
      setStatus('error')
      setErrorMessage('Please enter a 5-digit ZIP code')
      return
    }
    if (!EMAIL_REGEX.test(email)) {
      setStatus('error')
      setErrorMessage('Please enter a valid email address')
      return
    }

    setStatus('submitting')
    setErrorMessage(null)
    try {
      const grassTag = grassType ?? TOPIC_GRASS_TYPE[topic]
      const response = await fetch('/api/email/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          zipCode: zip,
          topic,
          ...(grassTag ? { grassType: grassTag } : {}),
          context: `${signupSource}${slug ? `:${slug}` : ''}`,
        }),
      })

      if (response.ok) {
        analytics.newsletterSignup(email, signupSource)
        setStatus('success')
        return
      }

      const payload = await response.json().catch(() => null)
      if (response.status === 409 || payload?.error === 'already_subscribed') {
        // Treat dup as success — same outcome from the user's POV
        analytics.newsletterSignup(email, `${signupSource}_returning`)
        setStatus('success')
        return
      }

      setStatus('error')
      setErrorMessage(payload?.error || 'Something went wrong. Please try again.')
    } catch {
      setStatus('error')
      setErrorMessage('Network error. Please try again.')
    }
  }

  if (status === 'success') {
    return (
      <div className="ds-glass my-8 mx-auto max-w-2xl rounded-[24px] p-6 sm:p-7">
        <div className="flex items-start gap-4">
          <div className="grid place-items-center rounded-[14px] border border-white/80 bg-linear-to-b from-white/90 to-green-50/70 text-green-600 w-12 h-12 flex-shrink-0 shadow-[0_6px_16px_rgba(22,163,74,0.14)]">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-editorial text-lg font-semibold text-gray-900">Your calendar is on the way</h3>
            <p className="text-sm text-gray-600 mt-1.5">
              Check your inbox for your 12-month lawn calendar, starting with this month. We saved ZIP {zip} to your
              {' '}{topic.replace(/-/g, ' ')} profile so your timing tips match your area.
            </p>
            <p className="text-sm text-gray-600 mt-3">
              Want full year-round reminders for every care window? Premium starts with a {PREMIUM_TRIAL_DAYS}-day trial.{' '}
              <a href="/pricing" className="font-semibold text-green-700 underline decoration-green-300 underline-offset-2 hover:text-green-800">
                Start {PREMIUM_TRIAL_DAYS}-day trial
              </a>
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={impressionRef} className="ds-glass my-8 mx-auto max-w-2xl rounded-[24px] p-6 sm:p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1">
      <div className="flex items-start gap-4 mb-4">
        <div className="grid place-items-center rounded-[14px] border border-white/80 bg-linear-to-b from-white/90 to-green-50/70 text-green-600 w-12 h-12 flex-shrink-0 shadow-[0_6px_16px_rgba(22,163,74,0.14)]">
          <BellRing className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-editorial text-lg sm:text-xl font-semibold text-gray-900 leading-tight">
            {copy.title}
          </h3>
          <p className="text-sm text-gray-600 mt-1.5 leading-snug">{copy.subtitle}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-green-200/80 bg-green-100/70 px-3 py-1.5 text-[13px] font-semibold text-green-800 backdrop-blur-sm">
          <Calendar className="w-3.5 h-3.5 text-green-600" aria-hidden="true" />
          Seasonal timing
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-green-200/80 bg-green-100/70 px-3 py-1.5 text-[13px] font-semibold text-green-800 backdrop-blur-sm">
          <MapPin className="w-3.5 h-3.5 text-green-600" aria-hidden="true" />
          Tuned to your ZIP
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-green-200/80 bg-green-100/70 px-3 py-1.5 text-[13px] font-semibold text-green-800 backdrop-blur-sm">
          <Bell className="w-3.5 h-3.5 text-green-600" aria-hidden="true" />
          12-month calendar
        </span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <ZipCodeInput
          value={zip}
          onChange={setZip}
          showLabel={false}
          rememberLastZip
        />
        <Input
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="you@example.com"
          value={email}
          onChange={handleEmailChange}
          aria-label="Email address"
          className="h-auto rounded-[10px] border-[1.5px] border-gray-300 bg-white pl-4 py-3.5 text-base font-medium text-gray-900 placeholder:text-gray-400 transition-colors focus:border-green-500 focus-visible:ring-0 focus-visible:ring-offset-0"
          required
        />
        {emailSuggestion && (
          <div className="bg-amber-50/85 backdrop-blur-md border border-amber-200/70 rounded-[14px] p-3 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 text-sm text-amber-800 min-w-0">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" aria-hidden="true" />
              <span className="truncate">
                Did you mean <strong>{emailSuggestion.correctedDomain}</strong>?
              </span>
            </span>
            <span className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={handleAcceptSuggestion}
                className="text-sm font-medium text-green-700 hover:text-green-800 px-2 py-1 rounded-full hover:bg-green-50 transition-colors"
              >
                Yes, fix it
              </button>
              <button
                type="button"
                onClick={() => setEmailSuggestion(null)}
                className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                No
              </button>
            </span>
          </div>
        )}
        <Button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full rounded-full bg-linear-to-r from-green-600 to-emerald-600 text-white text-base font-bold whitespace-nowrap min-h-[52px] transition hover:-translate-y-0.5 active:translate-y-0 active:scale-95 shadow-[0_12px_26px_-8px_rgba(22,163,74,0.5)]"
        >
          {status === 'submitting' ? 'Sending...' : copy.cta}
        </Button>
      </form>

      {status === 'error' && errorMessage && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {errorMessage}
        </p>
      )}

      <p className="mt-3 text-xs text-gray-500">
        Seasonal reminders and tips. No spam. Unsubscribe anytime.
      </p>
    </div>
  )
}
